import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Building2,
  Activity,
  Users,
  Phone,
  ArrowRight,
  LogOut,
  Plus,
} from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useNavigate } from 'react-router-dom';
import { Campaign } from '@/types/campaign';

interface ClientMembership {
  id: string;
  role: string;
  clients: {
    id: string;
    name: string;
    domain: string | null;
    subdomain: string | null;
    status: string;
    branding: any;
  };
}

export default function PortalDashboard() {
  const { user, signOut, isOnlyMember } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<ClientMembership[]>([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [businessPortal, setBusinessPortal] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchClientMemberships()
        .then((clientIds) => {
          fetchBusinessPortal();
          fetchClientStats(clientIds);
        })
        .finally(() => setLoading(false));
    }
  }, [user]);

  const fetchBusinessPortal = async () => {
    try {
      // Fetch business portal for current user
      const { data, error } = await supabase
        .from('clients')
        .select('branding, domain, subdomain, name')
        .eq('user_id', user?.id)
        .eq('settings->>is_business_portal', 'true')
        .maybeSingle();
      if (error) {
        console.error('Error fetching business portal:', error);
        return;
      }
      setBusinessPortal(data);
    } catch (error: any) {
      console.error('Error fetching business portal:', error);
    }
  };

  const fetchClientMemberships = async (): Promise<string[]> => {
    try {
      // First, get memberships
      const { data: membershipData, error: membershipError } = await supabase
        .from('client_memberships')
        .select(
          `
          id,
          role,
          clients!inner(
            id,
            name,
            domain,
            subdomain,
            status,
            branding
          )
        `,
        )
        .eq('user_id', user?.id);
      if (membershipError) throw membershipError;
      // Then, get owned clients (exclude business portal)
      const { data: ownedData, error: ownedError } = await supabase
        .from('clients')
        .select('id, name, domain, subdomain, status, branding, settings')
        .eq('user_id', user?.id);
      if (ownedError) throw ownedError;
      // Filter out business portal on client side
      const filteredOwnedData = (ownedData || []).filter((client) => {
        const settings = client.settings as any;
        return settings?.is_business_portal !== true;
      });
      // Combine and deduplicate
      const existingIds = new Set(
        membershipData?.map((m) => m.clients.id) || [],
      );
      const ownedMemberships = filteredOwnedData
        .filter((client) => !existingIds.has(client.id))
        .map((client) => ({
          id: `owner-${client.id}`,
          role: 'owner',
          clients: {
            id: client.id,
            name: client.name,
            domain: client.domain,
            subdomain: client.subdomain,
            status: client.status,
            branding: client.branding,
          },
        }));
      const allMemberships = [...(membershipData || []), ...ownedMemberships];
      setMemberships(allMemberships);

      // Return client IDs for immediate use
      return allMemberships.map((m) => m.clients.id);
    } catch (error: any) {
      console.error('Error fetching client memberships:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your client access',
        variant: 'destructive',
      });
      return [];
    }
  };

  const fetchClientStats = async (clientIds?: string[]) => {
    if (!clientIds) {
      clientIds = memberships.map((m) => m.clients.id);
    }

    try {
      console.log('[PortalDashboard] fetchClientStats - clientIds:', clientIds);

      if (clientIds.length === 0) {
        console.log('[PortalDashboard] No client IDs, setting empty stats');
        setCampaignStats({});
        return;
      }

      // For now, just fetch campaign counts per client
      // We'll optimize calls/actions counting later
      const stats: any = {};

      for (const clientId of clientIds) {
        try {
          // Get campaign IDs for this client first
          const { data: clientCampaigns, error: campaignsQueryError } =
            await supabase
              .from('campaigns')
              .select('id')
              .eq('client_id', clientId);

          if (campaignsQueryError) {
            console.error(
              `Error fetching campaigns for client ${clientId}:`,
              campaignsQueryError,
            );
            stats[clientId] = { campaignCount: 0, calls: 0, callActions: 0 };
            continue;
          }

          const campaignIds = clientCampaigns?.map((c) => c.id) || [];
          const campaignCount = campaignIds.length;

          if (campaignIds.length === 0) {
            stats[clientId] = { campaignCount: 0, calls: 0, callActions: 0 };
            continue;
          }

          // Count actions for this client's campaigns (only active, user-visible actions)
          const userVisibleActionTypes = [
            'follow_up_call',
            'consent_callback',
            'email_provided',
            'email_promise',
            'appointment_interest',
            'appointment_booked',
            'message_taking',
          ];
          const { count: actionCount, error: actionError } = await supabase
            .from('call_actions')
            .select('*', { count: 'exact', head: true })
            .in('campaign_id', campaignIds)
            .eq('status', 'active')
            .in('type', userVisibleActionTypes);

          // Count distinct calls via call_actions (same data source as action center)
          const { data: callIdRows, error: callError } = await supabase
            .from('call_actions')
            .select('call_id')
            .in('campaign_id', campaignIds)
            .not('call_id', 'is', null);

          const uniqueCallCount = callIdRows
            ? new Set(callIdRows.map((r) => r.call_id)).size
            : 0;

          stats[clientId] = {
            campaignCount,
            calls: uniqueCallCount,
            callActions: actionCount || 0,
          };

          if (actionError) {
            console.error(
              `Error counting actions for client ${clientId}:`,
              actionError,
            );
          }
          if (callError) {
            console.error(
              `Error counting calls for client ${clientId}:`,
              callError,
            );
          }
        } catch (error) {
          console.error(`Error fetching stats for client ${clientId}:`, error);
          stats[clientId] = { campaignCount: 0, calls: 0, callActions: 0 };
        }
      }

      console.log('[PortalDashboard] Final stats:', stats);
      setCampaignStats(stats);
    } catch (error: any) {
      console.error('Error in fetchClientStats:', error);
      setCampaignStats({});
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) throw error;

      navigate('/portal/auth');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to sign out',
        variant: 'destructive',
      });
    }
  };

  const accessClient = (clientId: string, subdomain?: string) => {
    // Navigate to client-specific portal
    if (subdomain) {
      // Check if we need cross-origin navigation for white-label domains
      const targetOrigin = `https://${subdomain}.yourdomain.com`;
      if (window.location.origin === targetOrigin) {
        // Same origin - use SPA navigation
        navigate('/portal/actions');
      } else {
        // Cross-origin - use full navigation to preserve white-label independence
        window.location.assign(`${targetOrigin}/portal/actions`);
      }
    } else {
      // Use client ID routing for same-origin navigation
      navigate(`/portal/client/${clientId}/actions`);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-background to-muted/50 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  // Apply business portal branding if available
  const branding = businessPortal?.branding || {};
  const companyName =
    branding.companyName || businessPortal?.name || 'Client Portal';
  const primaryColor = branding.primaryColor || 'hsl(var(--primary))';

  return (
    <div className='min-h-screen bg-gradient-to-br from-background to-muted/50'>
      {/* Header */}
      <div className='border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center'>
                <Building2 className='w-4 h-4 text-primary' />
              </div>
              <div>
                <h1 className='text-xl font-semibold'>{companyName}</h1>
                <p className='text-sm text-muted-foreground'>
                  Welcome back, {user?.email}
                </p>
              </div>
            </div>

            <div className='flex items-center gap-2'>
              <ThemeToggle />
              <Button variant='outline' size='sm' onClick={handleSignOut}>
                <LogOut className='w-4 h-4 mr-1' />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className='container mx-auto px-4 py-8'>
        {memberships.length === 0 ? (
          <Card>
            <CardContent className='flex flex-col items-center justify-center py-12'>
              <Building2 className='h-12 w-12 text-muted-foreground mb-4' />
              <h3 className='text-lg font-medium text-foreground mb-2'>
                No Client Access
              </h3>
              <p className='text-muted-foreground text-center mb-4'>
                You don't have access to any client portals yet.{' '}
                {isOnlyMember
                  ? 'Contact your administrator to get started.'
                  : 'Create your first client to get started.'}
              </p>
              {!isOnlyMember && (
                <Button onClick={() => navigate('/clients')}>
                  <Plus className='w-4 h-4 mr-2' />
                  Create your first client
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className='space-y-6'>
            <div>
              <h2 className='text-2xl font-bold text-foreground mb-2'>
                Your Clients
              </h2>
              <p className='text-muted-foreground'>
                Select a client to access their campaign data and action center
              </p>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {memberships.map((membership) => {
                const client = membership.clients;
                const branding = client.branding || {};

                return (
                  <Card
                    key={membership.id}
                    className='group hover:shadow-lg transition-all duration-200 border-2 hover:border-primary/20'
                  >
                    <CardHeader>
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <CardTitle className='flex items-center gap-2 text-lg'>
                            <div
                              className='w-3 h-3 rounded-full'
                              style={{
                                backgroundColor:
                                  branding.primaryColor || primaryColor,
                              }}
                            />
                            {client.name}
                          </CardTitle>
                          <CardDescription className='mt-1'>
                            {client.domain ||
                              client.subdomain ||
                              'Client Portal'}
                          </CardDescription>
                        </div>
                        <Badge
                          variant={
                            client.status === 'active' ? 'default' : 'secondary'
                          }
                        >
                          {client.status}
                        </Badge>
                      </div>
                    </CardHeader>

                    <CardContent className='space-y-4'>
                      <div className='flex items-center justify-between text-sm'>
                        <span className='text-muted-foreground'>
                          Your Role:
                        </span>
                        <Badge variant='outline' className='capitalize'>
                          {membership.role}
                        </Badge>
                      </div>
                      {/* Quick Stats - Placeholder for now */}
                      <div className='grid grid-cols-3 gap-3 py-3 border-t border-b'>
                        <div className='text-center'>
                          <div className='text-sm font-medium'>
                            {campaignStats[client.id]?.campaignCount ?? 0}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            Campaigns
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-sm font-medium'>
                            {campaignStats[client.id]?.calls ?? 0}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            Calls
                          </div>
                        </div>
                        <div className='text-center'>
                          <div className='text-sm font-medium'>
                            {campaignStats[client.id]?.callActions ?? 0}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            Actions
                          </div>
                        </div>
                      </div>
                      <Button
                        className='w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors'
                        variant='outline'
                        onClick={() =>
                          accessClient(client.id, client.subdomain)
                        }
                      >
                        Access Portal
                        <ArrowRight className='w-4 h-4 ml-2' />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
